/**
 * Parlay sharing utilities.
 *
 * Parlays are client-side constructs (not backed by a single odd_id like EV/arb),
 * so we encode the full parlay data into a URL-safe base64 string that lives in
 * the URL path.  The Firebase cloud function decodes this same format to produce
 * rich OG / Twitter meta tags for crawlers.
 */

export type ParlayStyle = 'smart' | 'best_ev' | 'long_odds' | 'short_odds' | 'mixed';

export const PARLAY_STYLE_LABELS: Record<ParlayStyle, string> = {
  smart: 'Smart',
  best_ev: 'Best EV',
  long_odds: 'Long Shot',
  short_odds: 'Safe Play',
  mixed: 'Mixed',
};

export const PARLAY_STYLE_IDS: ParlayStyle[] = [
  'smart',
  'best_ev',
  'long_odds',
  'short_odds',
  'mixed',
];

export function isStyleId(s: string): s is ParlayStyle {
  return (PARLAY_STYLE_IDS as string[]).includes(s);
}

/* ─── Return-target tiers ─── */

export type ReturnTarget = 2 | 5 | 10 | 20 | 100;

export const RETURN_TARGETS: ReturnTarget[] = [2, 5, 10, 20, 100];

export const RECOMMENDED_TARGET: ReturnTarget = 2;

/** Target combined-decimal odds for a given return multiplier (e.g. 5x → 5). */
export function targetDecimalForReturn(t: ReturnTarget): number {
  return t;
}

export function isReturnTarget(n: number): n is ReturnTarget {
  return (RETURN_TARGETS as number[]).includes(n);
}

export interface SharedParlayLeg {
  e: string; // event
  m: string; // market
  p: string; // pick
  o: number; // odds (American)
  f: number; // fair odds (American)
  v: number; // EV%
  k: string; // bookie display name
  t: string; // sport
  d?: string; // event date (ISO) — optional; older shared links may omit
}

export interface SharedParlayData {
  s: ParlayStyle;
  b: string; // book key
  n: string; // book display name
  l: SharedParlayLeg[];
}

/**
 * Rich runtime shape used by the parlay-builder UI.  Both the live builder
 * and the share landing page render the same component, so we keep the
 * expanded-leg type here next to the URL-compact one.
 */
export interface ParlayLeg {
  event: string;
  market: string;
  pick: string;
  odds: number;
  fairOdds: number;
  evPercent: number;
  bookie: string;
  sport: string;
  eventDate: string;
  /**
   * For Kalshi legs, the exchange ticker. Lets the parlay UI render an
   * inline "Place on Kalshi" CTA per leg — including UNDERS, which the
   * consumer Kalshi app does not surface.
   */
  kalshiTicker?: string | null;
  /**
   * Net (post-fee) American odds for prediction-market legs. Equal to
   * {@link #odds} when missing or when the leg is on a sportsbook;
   * the parlay results UI shows this when the user toggles to "Net".
   */
  netOdds?: number | null;
  /**
   * True when the leg's `odds` / `fairOdds` / `evPercent` have been
   * refreshed from the live +EV feed (share landing page only). When
   * unset or false, the leg is showing the snapshot values that were
   * encoded at share-creation time.
   */
  isLive?: boolean;
  /**
   * Snapshot of the originally shared American odds. Set on the share
   * page after refreshing from live data so the UI can render a
   * "line moved" indicator when the live odds differ.
   */
  originalOdds?: number;
  /**
   * Snapshot of the originally shared EV%. Set on the share page after
   * refreshing so the UI can compare against the current EV%.
   */
  originalEvPercent?: number;
  /**
   * True when the leg could not be located in the live +EV feed
   * (line pulled, game started, market closed, etc.). The UI falls
   * back to the snapshot odds and renders a "Line unavailable" flag.
   */
  liveUnavailable?: boolean;
  /**
   * True when `evPercent` / `fairOdds` are assumed rather than
   * board-computed — Sharp Money legs carry no fair odds, so the slip
   * assumes the Sharp track's average realized return and backs fair
   * odds out of it. The slip UI flags these as estimates.
   */
  evEstimated?: boolean;
  /**
   * Board-time snapshot of every book quoting this same outcome
   * (including the leg's own book), so the parlay slip can re-quote
   * the pick at another book. Fair odds are book-independent, so a
   * re-quote only swaps `bookie`/`odds` and recomputes `evPercent`.
   */
  altBooks?: Array<{ bookie: string; odds: number }> | null;
  /**
   * Odds API event id of the leg's board row. Sent with a tracked
   * parlay so the backend can expand per-leg fingerprint rows — that's
   * what lights the leg's own "already bet" badge on the boards.
   */
  oddId?: string | null;
  /** Raw feed market key of the leg's row, same purpose as oddId. */
  marketKey?: string | null;
  /**
   * Pre-resolved deep-link URL into the sportsbook for this leg.
   * Comes from the +EV row's `outcomeLink` (when populated). The bet
   * agent extension prefers this over its own search heuristic, so
   * preserving it through the parlay-builder pipeline is what makes
   * "Add full parlay" actually open the sportsbook with the bet
   * already in the session slip instead of searching by team name.
   */
  outcomeLink?: string | null;
}

/** Expand the URL-compact shared leg shape into the rich runtime shape. */
export function expandSharedLeg(l: SharedParlayLeg): ParlayLeg {
  return {
    event: l.e,
    market: l.m,
    pick: l.p,
    odds: l.o,
    fairOdds: l.f,
    evPercent: l.v,
    bookie: l.k,
    sport: l.t,
    eventDate: l.d || '',
  };
}

export function expandSharedLegs(legs: SharedParlayLeg[]): ParlayLeg[] {
  return legs.map(expandSharedLeg);
}

/* ─── Math helpers (duplicated from parlay-builder to keep this module standalone) ── */

export function americanToDecimal(american: number): number {
  if (american > 0) return american / 100 + 1;
  return 100 / Math.abs(american) + 1;
}

export function decimalToAmerican(decimal: number): number {
  if (decimal >= 2) return Math.round((decimal - 1) * 100);
  return Math.round(-100 / (decimal - 1));
}

export function formatOddSigned(odd: number): string {
  return odd > 0 ? `+${odd}` : `${odd}`;
}

export function computeParlayStats(legs: SharedParlayLeg[]) {
  if (!legs.length) return { odds: 0, fairOdds: 0, ev: 0, decimalOdds: 1 };

  const combinedDecimal = legs.reduce((acc, l) => acc * americanToDecimal(l.o), 1);
  const combinedFairDecimal = legs.reduce((acc, l) => acc * americanToDecimal(l.f), 1);

  const impliedProb = (american: number) => {
    if (american > 0) return 100 / (american + 100);
    return Math.abs(american) / (Math.abs(american) + 100);
  };

  const parlayTrueProb = legs.reduce((acc, l) => acc * impliedProb(l.f), 1);
  const profitIfWin = combinedDecimal - 1;
  const ev = (parlayTrueProb * profitIfWin) - ((1 - parlayTrueProb) * 1);

  return {
    odds: decimalToAmerican(combinedDecimal),
    fairOdds: decimalToAmerican(combinedFairDecimal),
    ev: Math.round(ev * 10000) / 100,
    decimalOdds: combinedDecimal,
  };
}

/* ─── Encode / Decode ─── */

export function encodeParlayForShare(data: SharedParlayData): string {
  const json = JSON.stringify(data);
  const encoded = btoa(unescape(encodeURIComponent(json)));
  return encoded.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function decodeParlayFromShare(encoded: string): SharedParlayData | null {
  try {
    let padded = encoded.replace(/-/g, '+').replace(/_/g, '/');
    while (padded.length % 4 !== 0) padded += '=';
    const json = decodeURIComponent(escape(atob(padded)));
    const data = JSON.parse(json) as SharedParlayData;
    if (!data.l || !Array.isArray(data.l) || !data.l.length) return null;
    return data;
  } catch {
    return null;
  }
}

export const SHARE_PATH = '/parlay-builder/share/';
